// import { getAccessToken } from '@/utils/getAccessToken';
import { User } from '@privy-io/react-auth';
import { create } from 'zustand';

/* -------------------------------------------------------------------------- */

interface PrivyFunctions {
	getAccessToken: () => Promise<string | null>;
	linkDiscord: () => void;
	unlinkDiscord: (subject: string) => Promise<User>;
	linkEmail: () => void;
	unlinkEmail: (address: string) => Promise<User>;
	linkWallet: () => void;
	unlinkWallet: (address: string) => Promise<User>;
	updateEmail: () => void;
}

interface PrivyLoginFunctions {
	loginWithGoogle: () => void;
	loginWithDiscord: () => void;
	loginWithWallet: () => void;
}

interface AppStore {
	privyUser: User | null;
	authenticated: boolean;
	ready: boolean;
	accessToken: string | null;
	login: ((loginMethods?: object) => void) | null;
	logout: (() => void) | null;
	privyFunctions: PrivyFunctions | null;
	privyLoginFunctions: PrivyLoginFunctions | null;
	linkEmail: (() => void) | null;

	actions: {
		setPrivyUser: (user: User | null) => void;
		setReady: (ready: boolean) => void;
		setAuthenticated: (isAuthenticated: boolean) => void;
		setAccessToken: (accessToken: string) => void;
		setLogin: (login: (loginMethods: object) => void) => void;
		setLogout: (logout: () => void) => void;
		setPrivyFunctions: (privyFunctions: PrivyFunctions) => void;
		setPrivyLoginFunctions: (privyLoginFunctions: PrivyLoginFunctions) => void;
		setLinkEmail: (linkEmail: () => void) => void;
		resetPrivyStore: () => void;
	};
}

const initialState = {
	privyUser: null,
	authenticated: false,
	ready: false,
	accessToken: null,
	login: null,
	logout: null,
	privyFunctions: null,
	privyLoginFunctions: null,
	linkEmail: null,
};

const appStore = create<AppStore>((set) => ({
	...initialState,

	/* -------------------------------------------------------------------------- */

	actions: {
		setPrivyUser: (user: User | null) => {
			set(() => ({
				privyUser: user,
			}));
		},
		setReady: (ready: boolean) => {
			set(() => ({
				ready,
			}));
		},
		setAuthenticated: (authenticated: boolean) => {
			set(() => ({
				authenticated,
			}));
		},
		/* -------------------------------------------------------------------------- */
		setAccessToken: (accessToken: string | null) => {
			set(() => ({
				accessToken: `AccessToken=${accessToken}`,
			}));
		},

		setLogin: (login: (loginMethods: object) => void) => {
			set(() => ({
				login, // Store function reference instead of calling it
			}));
		},

		setLogout: (logout: () => void | null) => {
			set(() => ({
				logout,
			}));
		},
		setPrivyFunctions: (privyFunctions: PrivyFunctions) => {
			set(() => ({
				privyFunctions,
			}));
		},
		setPrivyLoginFunctions: (privyLoginFunctions: PrivyLoginFunctions) => {
			set(() => ({
				privyLoginFunctions,
			}));
		},

		setLinkEmail: (linkEmail: () => void | null) => {
			set(() => ({
				linkEmail,
			}));
		},
		/* -------------------------------------------------------------------------- */
		resetPrivyStore: () => {
			set(() => ({
				...initialState,
			}));
		},
	},
}));

export const usePrivyUser = () => appStore((state) => state.privyUser);
export const useLogin = () => appStore((state) => state.login);
export const useLogout = () => appStore((state) => state.logout);
export const useAccessToken = () => appStore((state) => state.accessToken);
export const useAuthenticated = () => appStore((state) => state.authenticated);
export const useReady = () => appStore((state) => state.ready);
export const usePrivyFunctions = () =>
	appStore((state) => state.privyFunctions);

export const usePrivyLoginFunctions = () =>
	appStore((state) => state.privyLoginFunctions);
/* -------------------------------------------------------------------------- */
export const useLinkEmail = () => appStore((state) => state.linkEmail);
/* -------------------------------------------------------------------------- */

export const usePrivyStoreActions = () => appStore((state) => state.actions);
